import React from "react";
import { CaptionElement } from "../../types";

interface CaptionEditingControlsProps {
  selectedCaption: CaptionElement;
  onCaptionUpdate: (id: string, updates: Partial<CaptionElement>) => void;
}

const CaptionEditingControls: React.FC<CaptionEditingControlsProps> = ({
  selectedCaption,
  onCaptionUpdate,
}) => {
  return (
    <div className="border-t border-gray-200 pt-3 mb-3">
      <h4
        className="font-bold text-gray-800 mb-2 text-center text-sm"
        dir="rtl"
      >
        עריכת כיתוב
      </h4>
      <div className="space-y-2">
        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            כיתוב
          </label>
          <input
            type="text"
            value={selectedCaption.text}
            onChange={(e) =>
              onCaptionUpdate(selectedCaption.id, { text: e.target.value })
            }
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent text-center"
            dir="rtl"
          />
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            גופן
          </label>
          <select
            value={selectedCaption.fontFamily}
            onChange={(e) =>
              onCaptionUpdate(selectedCaption.id, {
                fontFamily: e.target.value,
              })
            }
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            dir="rtl"
          >
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Franklin Gothic Medium">
              Franklin Gothic Medium
            </option>
            <option value="Impact">Impact</option>
            <option value="Comic Sans MS">Comic Sans MS</option>
            <option value="David">David (Hebrew)</option>
            <option value="Miriam">Miriam (Hebrew)</option>
            <option value="Noto Sans Hebrew">Noto Sans Hebrew</option>
            <option value="Heebo">Heebo (Hebrew)</option>
          </select>
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            כיוון כתיבה
          </label>
          <div className="flex gap-1">
            <button
              dir="rtl"
              onClick={() =>
                onCaptionUpdate(selectedCaption.id, { dir: "rtl" })
              }
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedCaption.dir === "rtl"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              RTL
            </button>
            <button
              dir="rtl"
              onClick={() =>
                onCaptionUpdate(selectedCaption.id, { dir: "ltr" })
              }
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                selectedCaption.dir === "ltr"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              LTR
            </button>
          </div>
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            יישור טקסט
          </label>
          <div className="flex gap-1">
            <button
              onClick={() =>
                onCaptionUpdate(selectedCaption.id, { align: "right" })
              }
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                (selectedCaption.align || "center") === "right"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              ימין
            </button>
            <button
              onClick={() =>
                onCaptionUpdate(selectedCaption.id, { align: "center" })
              }
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                (selectedCaption.align || "center") === "center"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              מרכז
            </button>
            <button
              onClick={() =>
                onCaptionUpdate(selectedCaption.id, { align: "left" })
              }
              className={`flex-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                (selectedCaption.align || "center") === "left"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              שמאל
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              צבע טקסט
            </label>
            <input
              type="color"
              value={selectedCaption.fill}
              onChange={(e) =>
                onCaptionUpdate(selectedCaption.id, { fill: e.target.value })
              }
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              צבע מסגרת
            </label>
            <input
              type="color"
              value={selectedCaption.stroke}
              onChange={(e) =>
                onCaptionUpdate(selectedCaption.id, { stroke: e.target.value })
              }
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              עובי: {selectedCaption.strokeWidth}px
            </label>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={selectedCaption.strokeWidth}
              onChange={(e) =>
                onCaptionUpdate(selectedCaption.id, {
                  strokeWidth: parseInt(e.target.value),
                })
              }
              className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #2563eb 0%, #1d4ed8 100%)`,
              }}
            />
          </div>
          <div>
            <label
              className="block text-xs font-medium text-gray-700 mb-1"
              dir="rtl"
            >
              גובה: {selectedCaption.height}px
            </label>
            <input
              type="range"
              min="60"
              max="200"
              step="10"
              value={selectedCaption.height}
              onChange={(e) =>
                onCaptionUpdate(selectedCaption.id, {
                  height: parseInt(e.target.value),
                })
              }
              className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #2563eb 0%, #1d4ed8 100%)`,
              }}
            />
          </div>
        </div>

        <div>
          <label
            className="block text-xs font-medium text-gray-700 mb-1"
            dir="rtl"
          >
            רוחב טקסט: {selectedCaption.width}px
          </label>
          <input
            type="range"
            min="100"
            max="500"
            step="10"
            value={selectedCaption.width}
            onChange={(e) =>
              onCaptionUpdate(selectedCaption.id, {
                width: parseInt(e.target.value),
              })
            }
            className="w-full h-1 bg-gray-200 rounded appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #2563eb 0%, #1d4ed8 100%)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CaptionEditingControls;
